import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

const noop = () => {};

const defaultValue = {
  register: noop,
  unregister: noop,
  focus: noop,
  moveFocus: noop,
};

const GridNavigationContext = createContext(defaultValue);

export const GridNavigationProvider = ({ children }) => {
  const cellsRef = useRef(new Map());

  const register = useCallback((cellId, meta) => {
    cellsRef.current.set(cellId, meta);
  }, []);

  const unregister = useCallback((cellId) => {
    cellsRef.current.delete(cellId);
  }, []);

  const focus = useCallback((cellId) => {
    const meta = cellsRef.current.get(cellId);
    if (meta) meta.focus();
  }, []);

  const moveFocus = useCallback((direction, fromId) => {
    const current = cellsRef.current.get(fromId);
    if (!current) return;
    const { rowIndex, colIndex } = current;

    const editableCells = [];
    cellsRef.current.forEach((meta, id) => {
      if (meta.editable) editableCells.push({ id, ...meta });
    });

    let target;

    if (direction === "right") {
      [target] = editableCells
        .filter((c) => c.rowIndex === rowIndex && c.colIndex > colIndex)
        .sort((a, b) => a.colIndex - b.colIndex);
      if (!target) {
        [target] = editableCells
          .filter((c) => c.rowIndex > rowIndex)
          .sort((a, b) => a.rowIndex - b.rowIndex || a.colIndex - b.colIndex);
      }
    } else if (direction === "left") {
      [target] = editableCells
        .filter((c) => c.rowIndex === rowIndex && c.colIndex < colIndex)
        .sort((a, b) => b.colIndex - a.colIndex);
      if (!target) {
        [target] = editableCells
          .filter((c) => c.rowIndex < rowIndex)
          .sort((a, b) => b.rowIndex - a.rowIndex || b.colIndex - a.colIndex);
      }
    } else if (direction === "down") {
      [target] = editableCells
        .filter((c) => c.colIndex === colIndex && c.rowIndex > rowIndex)
        .sort((a, b) => a.rowIndex - b.rowIndex);
    } else if (direction === "up") {
      [target] = editableCells
        .filter((c) => c.colIndex === colIndex && c.rowIndex < rowIndex)
        .sort((a, b) => b.rowIndex - a.rowIndex);
    }

    if (target) target.focus();
  }, []);

  const value = useMemo(
    () => ({ register, unregister, moveFocus, focus }),
    [register, unregister, moveFocus, focus]
  );

  return (
    <GridNavigationContext.Provider value={value}>
      {children}
    </GridNavigationContext.Provider>
  );
};

export const useGridNavigation = () => useContext(GridNavigationContext);
